import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { deleteCoupon } from "../../Redux/Reducers/CoupansReducer";
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import {
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    TextField,
    TablePagination,
    CircularProgress,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import axios from 'axios';
import PageHeader1 from '../../components/common/PageHeader1';
import { getIntParam, getStringParam, setIfTruthy } from '../../utils/urlState';

const formatDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
        const datePart = value.split('T')[0];
        const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) {
            const [, year, month, day] = match;
            return `${day}/${month}/${year}`;
        }
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatDateTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('en-IN');
};

function ListConsultationCoupon() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const didInitUrlSyncRef = React.useRef(false);

    const [coupons, setCoupons] = useState([]);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(getIntParam(searchParams, "tab", 0));
    const [open, setOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [couponDetail, setCouponDetail] = useState(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [paginationModel, setPaginationModel] = useState({
        page: getIntParam(searchParams, "page", 0),
        pageSize: getIntParam(searchParams, "pageSize", 10),
    });
    const [paginationDetail, setPaginationDetail] = useState({});
    // Filter States
    const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [appliedSearchQuery, setAppliedSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [searchType, setSearchType] = useState(getStringParam(searchParams, "searchType", "couponCode"));
    const [appliedSearchType, setAppliedSearchType] = useState(getStringParam(searchParams, "searchType", "couponCode"));
    const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [appliedStartDate, setAppliedStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [appliedEndDate, setAppliedEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const applySawLoadingRef = React.useRef(false);
    const isInitialMount = React.useRef(true);
    const lastParamsRef = React.useRef(null);
    const [lastAction, setLastAction] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const CATEGORY_TABS = [
        // { label: "Flat Rs Off", value: "flat_off_rupees" },
        // { label: "Percent Off", value: "flat_off_percentage" },
        { label: "Flat Rs Extra", value: "flat_extra_rupees" },
        { label: "Percent Extra", value: "flat_extra_percentage" },
    ];

    const fetchCoupons = async () => {
        const token = localStorage.getItem("astroVani-admin-token");
        if (!token) {
            setError("Authentication token not found.");
            setStatus("error");
            return;
        }

        setStatus('loading');
        try {
            const { page, pageSize } = paginationModel;
            let limit = Number(pageSize);
            if (isNaN(limit)) {
                limit = 10;
            }
            const params = new URLSearchParams({
                page: (page + 1).toString(),
                limit: limit.toString(),
            });
            // Add category filter based on tab
            if (tabValue === 0) {
                params.append('category', 'flat_extra_rupees');
            } else if (tabValue === 1) {
                params.append('category', 'flat_extra_percentage');
            } else if (tabValue === 2) {
                params.append('category', 'flat_off_rupees');
            } else if (tabValue === 3) {
                params.append('category', 'flat_off_percentage');
            }
            if (appliedSearchQuery && appliedSearchQuery.trim()) {
                params.append(appliedSearchType, appliedSearchQuery.trim());
            }
            if (appliedStartDate) {
                params.append('startDate', appliedStartDate);
            }
            if (appliedEndDate) {
                params.append('endDate', appliedEndDate);
            }
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/coupon?${params.toString()}`,
                { headers: { 'Authorization': token } }
            );
            const data = response.data;
            setCoupons(data?.data || []);
            setPaginationDetail(data?.paginationDetail || {});
            setStatus('success');
            setError(null);
        } catch (err) {
            setError(err.message);
            setStatus('error');
            setCoupons([]);
            setPaginationDetail({});
        }
    };

    useEffect(() => {
        fetchCoupons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel.page, paginationModel.pageSize, tabValue, appliedSearchQuery, appliedSearchType, appliedStartDate, appliedEndDate, lastAction]);

    // Apply button should stay disabled until request completes.
    useEffect(() => {
        if (!isApplyingFilters) return;
        if (status === 'loading') {
            applySawLoadingRef.current = true;
            return;
        }
        if (status !== 'loading' && applySawLoadingRef.current) {
            applySawLoadingRef.current = false;
            setIsApplyingFilters(false);
        }
    }, [status, isApplyingFilters]);

    // Keep URL in sync so Back restores pagination/filters.
    useEffect(() => {
        if (!didInitUrlSyncRef.current) {
            didInitUrlSyncRef.current = true;
            return;
        }
        const params = new URLSearchParams();
        params.set("tab", String(tabValue));
        params.set("page", String(paginationModel.page));
        params.set("pageSize", String(paginationModel.pageSize));
        if (appliedSearchQuery && appliedSearchQuery.trim()) {
            params.set("searchType", appliedSearchType);
            params.set("search", appliedSearchQuery.trim());
        }
        setIfTruthy(params, "startDate", appliedStartDate);
        setIfTruthy(params, "endDate", appliedEndDate);
        setSearchParams(params, { replace: true });
    }, [tabValue, paginationModel.page, paginationModel.pageSize, appliedSearchQuery, appliedSearchType, appliedStartDate, appliedEndDate, setSearchParams]);

    const handleTabChange = (newValue) => {
        setTabValue(newValue);
        setPaginationModel({ page: 0, pageSize: 10 });
    };

    const handleChangePage = (event, newPage) => {
        setPaginationModel({ ...paginationModel, page: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
        const value = event.target.value;
        const newPageSize = parseInt(value, 10);
        setPaginationModel({ page: 0, pageSize: newPageSize });
    };

    // Apply Filters Handler
    const handleApplyFilters = () => {
        setIsApplyingFilters(true);
        applySawLoadingRef.current = false;
        setAppliedSearchQuery(searchQuery.trim());
        setAppliedSearchType(searchType);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setLastAction("apply");
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    // Reset Filter Handler
    const handleResetFilter = () => {
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setSearchQuery("");
        setAppliedSearchQuery("");
        setSearchType("couponCode");
        setAppliedSearchType("couponCode");
        setLastAction(null);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const handleStatusChange = async (id, newStatus) => {
        // Optimistic UI Update - "PhD Level" UX (Instant feedback)
        setCoupons((prev) =>
            prev.map((coupon) =>
                coupon._id === id ? { ...coupon, isActive: newStatus } : coupon
            )
        );

        try {
            const token = localStorage.getItem("astroVani-admin-token");
            if (!token) {
                toast.error("Authentication token not found.");
                return;
            }

            await axios.patch(
                `${process.env.REACT_APP_BASEURL}/admin/coupon/${id}`,
                { isActive: newStatus },
                { headers: { 'Authorization': token } }
            );
            // Success: Do nothing ensuring "pseudo effect" is maintained without list refresh
        } catch (err) {
            console.error("Status update failed:", err);
            toast.error("Network error: Failed to update status");
            // Revert Optimistic Update on failure - "toggle ko off/on karna"
            setCoupons((prev) =>
                prev.map((coupon) =>
                    coupon._id === id ? { ...coupon, isActive: !newStatus } : coupon
                )
            );
        }
    };

    const handleAppVisibilityChange = async (id, newVisibility) => {
        // Optimistic UI Update
        setCoupons((prev) =>
            prev.map((coupon) =>
                coupon._id === id ? { ...coupon, isVisibleOnApp: newVisibility } : coupon
            )
        );

        try {
            const token = localStorage.getItem("astroVani-admin-token");
            if (!token) {
                toast.error("Authentication token not found.");
                return;
            }

            const response = await axios.patch(
                `${process.env.REACT_APP_BASEURL}/admin/coupon/app-visibility/${id}`,
                { isVisibleOnApp: newVisibility },
                {
                    headers: {
                        'Authorization': token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            toast.success(response?.data?.message || "App visibility updated successfully");
        } catch (err) {
            console.error("App visibility update failed:", err);
            toast.error(err?.response?.data?.message || "Failed to update app visibility");
            // Revert Optimistic Update on failure
            setCoupons((prev) =>
                prev.map((coupon) =>
                    coupon._id === id ? { ...coupon, isVisibleOnApp: !newVisibility } : coupon
                )
            );
        }
    };

    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            if (!token) {
                toast.error("Authentication token not found.");
                return;
            }

            const buildCouponsUrl = () => {
                const params = new URLSearchParams();

                // Category filter based on tab
                if (tabValue === 0) {
                    params.append('category', 'flat_extra_rupees');
                } else if (tabValue === 1) {
                    params.append('category', 'flat_extra_percentage');
                } else if (tabValue === 2) {
                    params.append('category', 'flat_off_rupees');
                } else if (tabValue === 3) {
                    params.append('category', 'flat_off_percentage');
                }

                // Match the list filters (applied states), not the "typing" states.
                if (appliedSearchQuery && appliedSearchQuery.trim()) {
                    params.append(appliedSearchType || "couponCode", appliedSearchQuery.trim());
                }
                setIfTruthy(params, "startDate", appliedStartDate);
                setIfTruthy(params, "endDate", appliedEndDate);

                return `${process.env.REACT_APP_BASEURL}/admin/coupon?${params.toString()}`;
            };

            const url = buildCouponsUrl();
            const response = await axios.get(url, { headers: { 'Authorization': token } });
            const couponsData = response.data?.data || [];

            if (couponsData.length === 0) {
                return;
            }

            const exportData = couponsData.map((item, index) => ({
                'S.No': index + 1,
                'Coupon Code': item?.couponCode || '',
                'Category': item?.category || '',
                'Discount Value': item?.discountValue || '',
                'Discount Type': item?.discountType || '',
                'Valid From': item?.validFrom ? formatDate(item.validFrom) : '',
                'Valid To': item?.validTo ? formatDate(item.validTo) : '',
                'Status': item?.isActive ? 'Active' : 'Inactive',
                'Visible On App': item?.isVisibleOnApp ? 'Yes' : 'No',
                'Created At': item?.createdAt ? formatDateTime(item.createdAt) : '',
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Coupons');

            const columnWidths = [
                { wch: 8 },
                { wch: 20 },
                { wch: 25 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 15 },
                { wch: 25 },
            ];
            worksheet['!cols'] = columnWidths;

            XLSX.writeFile(workbook, 'consultation_coupons_export.xlsx');
            toast.success('Coupons exported successfully!');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export coupons');
        } finally {
            setIsExporting(false);
        }
    };

    const handleClickOpen = (userId) => {
        setSelectedUserId(userId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleViewDetail = async (id) => {
        setDetailOpen(true);
        setIsDetailLoading(true);
        setCouponDetail(null);
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            if (!token) {
                toast.error("Authentication token not found.");
                setIsDetailLoading(false);
                return;
            }
            const response = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/coupon/${id}`, {
                headers: { 'Authorization': token }
            });
            setCouponDetail(response.data?.data || response.data || null);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch coupon details");
            setDetailOpen(false);
        } finally {
            setIsDetailLoading(false);
        }
    };

    const handleCloseDetail = () => {
        setDetailOpen(false);
        setCouponDetail(null);
    };

    const handleDelete = async () => {
        try {
            await dispatch(deleteCoupon(selectedUserId));
            await fetchCoupons();
            setOpen(false);
            toast.success('Coupon deleted successfully!');
        } catch (err) {
            toast.error('Failed to delete coupon');
        }
    };

    const rows = useMemo(() => {
        const pageSize = paginationModel.pageSize;
        return Array.isArray(coupons)
            ? coupons.map((item, index) => ({
                id: item._id || index,
                _id: item._id,
                sono: paginationModel.page * pageSize + index + 1,
                couponCode: item?.couponCode,
                category: item?.category,
                discountValue: item?.discountValue,
                discountType: item?.discountType,
                validFrom: item?.validFrom || null,
                validTo: item?.validTo || null,
                createdAt: item?.createdAt || null,
                isActive: item?.isActive,
                isVisibleOnApp: item?.isVisibleOnApp ?? false,
            }))
            : [];
    }, [coupons, paginationModel]);

    const columns = [
        {
            field: 'sono',
            headerName: 'S.No',
            width: 80,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                        color="primary"
                        onClick={() => handleViewDetail(params.row._id)}
                        size="small"
                    >
                        <i className="icofont-eye-alt" style={{ color: '#0d6efd' }}></i>
                    </IconButton>
                    {/* <IconButton
                        color="error"
                        onClick={() => handleClickOpen(params.row._id)}
                        size="small"
                    >
                        <i className="icofont-ui-delete" style={{ color: '#dc3545' }}></i>
                    </IconButton> */}
                </Box>
            ),
        },
        {
            field: 'couponCode',
            headerName: 'Coupon Code',
            width: 180,
        },
        {
            field: 'category',
            headerName: 'Category',
            width: 180,
        },
        {
            field: 'discountValue',
            headerName: 'Discount',
            width: 120,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'validFrom',
            headerName: 'Start Date',
            width: 150,
            renderCell: (params) => formatDate(params.row?.validFrom),
        },
        {
            field: 'validTo',
            headerName: 'End Date',
            width: 150,
            renderCell: (params) => formatDate(params.row?.validTo),
        },
        {
            field: 'isActive',
            headerName: 'Status',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Switch
                    checked={params.value || false}
                    onChange={(e) => handleStatusChange(params.row._id, e.target.checked)}
                    inputProps={{ 'aria-label': 'controlled' }}
                />
            )
        },
        {
            field: 'isVisibleOnApp',
            headerName: 'Visible On App',
            width: 160,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Switch
                    checked={Boolean(params.value)}
                    onChange={(e) => handleAppVisibilityChange(params.row._id, e.target.checked)}
                    inputProps={{ 'aria-label': 'app-visibility' }}
                    color="primary"
                />
            )
        },
        {
            field: 'createdAt',
            headerName: 'Created At',
            width: 200,
            renderCell: (params) => {
                const dateVal = params.row?.createdAt;
                if (!dateVal) return '';
                return new Date(dateVal).toLocaleString('en-IN');
            }
        },
    ];

    return (
        <div className="body d-flex">
            <div className="container-xxl">
                <PageHeader1
                    pagetitle='Consultation Coupon List'
                    righttitle='Add Coupon'
                    link='/consultation-add'
                    routebutton={true}
                />

                {/* Toolbar with Filters */}
                <div className="card mb-3" style={{
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: 'none'
                }}>
                    <div className="card-body" style={{ padding: '16px 20px' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Search By</InputLabel>
                                <Select value={searchType} label="Search By" onChange={(e) => setSearchType(e.target.value)}>
                                    <MenuItem value="couponCode">Coupon Code</MenuItem>
                                    {/* <MenuItem value="category">Category</MenuItem> */}
                                </Select>
                            </FormControl>
                            <TextField
                                placeholder="Search..."
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                                sx={{ width: 200 }}
                            />
                            <TextField label="Start Date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <TextField label="End Date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <Button variant="contained" onClick={handleApplyFilters} disabled={status === 'loading' || isApplyingFilters}>
                                {isApplyingFilters ? "Applying..." : "Apply Filters"}
                            </Button>
                            <Button variant="outlined" onClick={handleResetFilter}>Reset</Button>
                            <Button variant="contained" color="success" onClick={exportToExcel} disabled={isExporting}>
                                {isExporting ? 'Exporting...' : 'Export'}
                            </Button>
                        </Box>
                    </div>
                </div>

                {/* Tabs Card */}
                <div className="card mb-3" style={{
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: 'none'
                }}>
                    <div className="card-body" style={{ padding: '16px 20px' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            {CATEGORY_TABS.map((tab, index) => (
                                <Button
                                    key={tab.value}
                                    variant={tabValue === index ? "contained" : "outlined"}
                                    onClick={() => handleTabChange(index)}
                                    sx={{
                                        fontSize: '14px',
                                        minHeight: 36,
                                        padding: '4px 16px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        borderRadius: '8px',
                                        backgroundColor: tabValue === index
                                            ? 'var(--primary-color, #E7B242)'
                                            : 'transparent',
                                        color: tabValue === index
                                            ? '#ffffff'
                                            : 'rgba(0, 0, 0, 0.6)',
                                        borderColor: tabValue === index
                                            ? 'var(--primary-color, #E7B242)'
                                            : 'rgba(0, 0, 0, 0.23)',
                                        '&:hover': {
                                            backgroundColor: tabValue === index
                                                ? 'var(--primary-color, #E7B242)'
                                                : 'rgba(0, 0, 0, 0.04)',
                                            color: tabValue === index
                                                ? '#ffffff'
                                                : 'var(--primary-color, #E7B242)',
                                            borderColor: 'var(--primary-color, #E7B242)',
                                        },
                                    }}
                                >
                                    {tab.label}
                                </Button>
                            ))}
                        </Box>

                    </div>
                </div>

                {/* Data Grid */}
                <div className="row g-0 mb-3">
                    <div className="col-md-12">
                        <div className="card" style={{
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: 'none',
                            overflow: 'hidden'
                        }}>
                            <div className="card-body" style={{ padding: 0, position: 'relative' }}>
                                {error && (
                                    <div className="alert alert-danger m-3" style={{ borderRadius: '8px' }}>
                                        <strong>Error:</strong> {error}
                                    </div>
                                )}
                                <Box sx={{
                                    width: '100%',
                                    '& .MuiDataGrid-root': {
                                        border: 'none',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f0f0f0',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f8f9fa',
                                        borderBottom: '2px solid #e9ecef',
                                        fontWeight: 600,
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        display: 'none',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f8f9fa',
                                    },
                                }}>
                                    <DataGrid
                                        columns={columns}
                                        rows={rows}
                                        loading={status === 'loading'}
                                        paginationMode="server"
                                        hideFooterPagination
                                        getRowId={(row) => row.id}
                                        columnVisibilityModel={columnVisibilityModel}
                                        onColumnVisibilityModelChange={(newModel) =>
                                            setColumnVisibilityModel(newModel)
                                        }
                                        disableSelectionOnClick
                                        autoHeight
                                        sx={{
                                            '& .MuiDataGrid-cell:focus': {
                                                outline: 'none',
                                            },
                                            '& .MuiDataGrid-cell:focus-within': {
                                                outline: 'none',
                                            },
                                        }}
                                    />
                                </Box>
                                <TablePagination
                                    component="div"
                                    count={paginationDetail?.totalDocs || 0}
                                    page={paginationModel.page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={paginationModel.pageSize}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[10, 25, 50]}
                                    labelRowsPerPage="Rows per page:"
                                    sx={{
                                        borderTop: '1px solid #e9ecef',
                                        '& .MuiTablePagination-toolbar': {
                                            padding: '12px 20px',
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this coupon?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Detail Dialog */}
            <Dialog open={detailOpen} onClose={handleCloseDetail} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, pb: 1, fontWeight: 600 }}>
                    Coupon Details
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDetail}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <i className="icofont-close"></i>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {isDetailLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                            <CircularProgress />
                        </Box>
                    ) : couponDetail ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Coupon Code:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.couponCode || '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Coupon Name:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.couponName || '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Platforms:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.platforms?.join(", ") || "-"}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Description:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.description || '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Category:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.category || '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Discount Value:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.discountValue ?? '0'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Discount Type:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.discountType || '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Extra Gift Amount:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.extraGiftAmount ?? '0'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Min Actual Amount:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.minActualAmount ?? '0'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Pack Mapping Type:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.packMappingType || '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Recharge Category:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.rechargeCategory || '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Max Uses Per User:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.maxUsesPerUser ?? '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Valid From:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.validFrom ? formatDate(couponDetail.validFrom) : '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Valid To:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.validTo ? formatDate(couponDetail.validTo) : '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Status:</strong>
                                <span style={{ fontWeight: 500, color: couponDetail?.isActive ? '#28a745' : '#dc3545' }}>{couponDetail?.isActive ? 'Active' : 'Inactive'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Visible On App:</strong>
                                <span style={{ fontWeight: 500, color: couponDetail?.isVisibleOnApp ? '#28a745' : '#dc3545' }}>{couponDetail?.isVisibleOnApp ? 'Yes' : 'No'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Created At:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.createdAt ? new Date(couponDetail.createdAt).toLocaleString('en-IN') : '-'}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 1 }}>
                                <strong style={{ color: '#666' }}>Updated At:</strong>
                                <span style={{ fontWeight: 500 }}>{couponDetail?.updatedAt ? new Date(couponDetail.updatedAt).toLocaleString('en-IN') : '-'}</span>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ p: 2, textAlign: 'center', color: '#888' }}>
                            Could not load coupon details.
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetail} variant="contained" color="primary" disableElevation>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ListConsultationCoupon;
